import { useEffect, useState } from "react";
import ContentsTopHeader from "../../../components/common/contents.top.header";
import { BackupFileListContainer } from "../../../style/contents/backup/backup";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  fetchBackupContentsAsOfBackupTime,
  fetchBackupUserListLoad,
  // 2026-07-02: 날짜 선택 화면에서 선택한 백업 시점 기준 조회로 전환했습니다.
  // 기존 현재 경로 기준 조회 API는 이후 폴더 최신 목록 조회 기능에서 다시 사용할 수 있어 주석으로 남깁니다.
  // fetchBackupFileList,
  // fetchBackupFileListUser,
} from "../../../api/admin";
import UserInfo from "../../../components/backup/user.info";
import FileList from "../../../components/backup/file.list";
import type { BackupUser } from "./backup.user";
import { fetchLoadClientLogin } from "../../../api/auth";
// 2026-07-02: fetchBackupFileList를 사용하는 admin 대리 로그인 흐름 복구 가능성을 위해 import 위치를 남깁니다.
// import { useUserLoginToken } from "../../../hooks/useUserLoginToken";
import PathBreadcrumb from "../../../components/common/bread.crumb";
import SearchForm from "../../../components/common/search.form";
import { useAuthStore } from "../../../atoms/user.auth.store";

export type FileType = "d" | "f";

export interface BackupFileItem {
  createDate: string;
  fileName: string;
  fileType: FileType;
  path: string;
  size: number;
  fileId?: number;
  backupHistoryId?: number;
  backupDate?: string;
  s3Url?: string;
  isWorm?: boolean;
}

const BackupFileList = () => {
  const { role } = useAuthStore();

  const [fileList, setFileList] = useState<BackupFileItem[]>([]);
  const [userData, setUserData] = useState<BackupUser[]>([]);
  const [loginToken, setLoginToken] = useState("");
  const initialPath = localStorage.getItem("initialPath") ?? "";

  const [searchKeyword, setSearchKeyword] = useState("");
  const [pageSize, setPageSize] = useState("10");
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const { path: rawPath = "" } = useParams<{ path: string }>();
  const currentPath = decodeURIComponent(rawPath); // 이걸 직접 fetch에 사용

  const queryParams = new URLSearchParams(location.search);
  const deviceId = queryParams.get("deviceId");
  const id = queryParams.get("id");
  const userAccount = queryParams.get("userAccount");
  const backupDate = queryParams.get("backupDate");
  const pcName = decodeURIComponent(atob(queryParams.get("pcName") || ""));
  const rawDirectoryInfo = localStorage.getItem("directory");
  const directoryInfo = rawDirectoryInfo ? JSON.parse(rawDirectoryInfo) : null;

  useEffect(() => {
    if (role !== "admin") return;

    const loadUserList = async () => {
      const userId = localStorage.getItem("userId");
      const companyCode = localStorage.getItem("companyCode");

      try {
        const response = await fetchBackupUserListLoad(
          Number(userId),
          companyCode || "",
        );
        setUserData(response.userList);
      } catch (err) {
        console.error("사용자 목록 로딩 실패:", err);
      }
    };

    loadUserList();
  }, [role]);

  useEffect(() => {
    if (role !== "admin" || userData.length === 0 || !userAccount || !id) {
      return;
    }

    const loadUserLogin = async () => {
      const companyCode = localStorage.getItem("companyCode");
      const matchedUser = userData.find(
        (user) => user.userAccount === userAccount,
      );

      if (!matchedUser) {
        console.warn("해당 userAccount에 대한 유저 정보를 찾을 수 없습니다.");
        setLoginToken("");
        return;
      }

      try {
        const response = await fetchLoadClientLogin({
          companyCode: companyCode || "",
          userAccount: matchedUser.userAccount,
          password: matchedUser.password,
          deviceId: "",
        });

        setLoginToken(response.jwtToken);
      } catch (err) {
        console.error("로그인 실패:", err);
        setLoginToken("");
      }
    };

    loadUserLogin();
  }, [id, role, userAccount, userData]);

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    // TODO: fetchData(keyword, pageSize);
  };

  const handlePageSizeChange = (size: string) => {
    setPageSize(size);
  };

  const handleFolderClick = (newPath: string) => {
    const encodedPath = encodeURIComponent(newPath); // remove leading slash
    const queryParams = new URLSearchParams(location.search);
    navigate(`/backup/file/list/${encodedPath}?${queryParams.toString()}`);
  };

  // 📌 1. 저장
  useEffect(() => {
    const saved = localStorage.getItem("initialPath");
    if (!saved && currentPath) {
      localStorage.setItem("initialPath", currentPath);
    }
  }, [currentPath]);

  /*
   * 2026-07-02: 날짜 선택 화면에서 선택한 백업 시점의 파일 목록을 조회해야 해서
   * fetchBackupContentsAsOfBackupTime 호출로 전환했습니다.
   * 아래 기존 fetchBackupFileList/fetchBackupFileListUser 흐름은 최신 폴더 기준 파일 목록 조회를
   * 다시 사용할 가능성이 있어 삭제하지 않고 보존합니다.
   *
   * useEffect(() => {
   *   const loadFileList = async () => {
   *     if (!deviceId || !id || !loginToken) return;
   *
   *     setIsLoading(true);
   *     try {
   *       const response = await fetchBackupFileList(
   *         deviceId,
   *         currentPath,
   *         id,
   *         loginToken,
   *       );
   *       setFileList(response.filePathlist || []);
   *     } catch (err) {
   *       console.error("파일 목록 조회 실패:", err);
   *     } finally {
   *       setIsLoading(false);
   *     }
   *   };
   *
   *   if (loginToken && role === "admin") loadFileList();
   * }, [loginToken, currentPath, deviceId, id]);
   *
   * useEffect(() => {
   *   const loadFileList = async () => {
   *     if (!deviceId || !id) return;
   *
   *     setIsLoading(true);
   *     try {
   *       const res = await fetchBackupFileListUser(deviceId, currentPath, id);
   *       setFileList(res.filePathlist || []);
   *     } catch (err) {
   *       console.error("파일 목록 조회 실패:", err);
   *     } finally {
   *       setIsLoading(false);
   *     }
   *   };
   *
   *   if (role === "user") loadFileList();
   * }, [currentPath, deviceId, id, role]);
   */

  useEffect(() => {
    const loadFileListAsOfBackupTime = async () => {
      if (!deviceId || !id || !backupDate) return;

      setIsLoading(true);
      try {
        const response = await fetchBackupContentsAsOfBackupTime(id, {
          deviceId,
          directoryPath: currentPath,
          backupDate,
          isAdmin: role === "admin",
        });
        setFileList(response.filePathlist || []);
      } catch (err) {
        console.error("백업 시점 파일 목록 조회 실패:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadFileListAsOfBackupTime();
  }, [backupDate, currentPath, deviceId, id, role]);

  // 검색 필터
  const filteredFileList = fileList.filter((file) => {
    const keyword = searchKeyword.toLowerCase();

    const fileTypeLabel = file.fileType === "d" ? "폴더" : "파일";

    return (
      file.fileName.toLowerCase().includes(keyword) ||
      file.createDate.toLowerCase().includes(keyword) ||
      file.path.toLowerCase().includes(keyword) ||
      file.size.toString().includes(keyword) ||
      fileTypeLabel.includes(keyword) // ✅ 파일/폴더 한글 검색 지원
    );
  });

  return (
    <BackupFileListContainer>
      {" "}
      <ContentsTopHeader
        title="디바이스 목록 > 백업 파일 목록"
        backArrowPath="-1"
      />
      <div className="contents-top">
        <div className="device-name">
          <p>
            {pcName}({deviceId})
          </p>
        </div>
        <div className="device-info">
          <p>마지막 백업시간 : {directoryInfo?.createDate || backupDate}</p>
          {role === "admin" && <UserInfo userAccount={userAccount || ""} />}
        </div>
      </div>
      <div className="file-list-container">
        <div className="top">
          <div className="path-name">
            <PathBreadcrumb
              initialPath={initialPath ?? ""}
              currentPath={currentPath}
              onClick={handleFolderClick}
            />
          </div>
          <div className="right">
            <SearchForm
              onSearch={handleSearch}
              onPageSizeChange={handlePageSizeChange}
              pageSize={pageSize}
            />
          </div>
        </div>
        {fileList ? (
          <FileList
            data={filteredFileList}
            pageSize={parseInt(pageSize)}
            onFolderClick={handleFolderClick} // ✅ 경로 클릭 처리
            id={Number(id)}
            deviceId={deviceId || ""}
            isLoading={isLoading} // ✅ 전달
            role={role || ""}
            loginToken={loginToken}
          />
        ) : (
          <div>
            <p>로딩 중입니다.</p>
          </div>
        )}
      </div>
    </BackupFileListContainer>
  );
};

export default BackupFileList;
